import { Modal } from "antd";
import React, { ReactNode } from "react";
import { createRoot } from "react-dom/client";
import Button from "../Button";
import CloseIcon from "./assets/close-icon.svg";

type ConfirmModalProps = {
  open: boolean;
  title: ReactNode;
  content: string;
  continueText?: string;
  backText?: string;
  onContinue: () => void;
  onBack: () => void;
  width?: number;
  backButtonWidth?: number;
  confirmCountdown?: number;
};

export function ConfirmModal(props: ConfirmModalProps) {
  const {
    open,
    title,
    content,
    continueText = "Continue",
    backText = "Back",
    onContinue,
    onBack,
    width = 360,
    backButtonWidth,
    confirmCountdown,
  } = props;

  const [countdown, setCountdown] = React.useState(confirmCountdown || 0);

  React.useEffect(() => {
    if (!open || !confirmCountdown) {
      setCountdown(confirmCountdown || 0);
      return;
    }

    if (countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [open, countdown, confirmCountdown]);

  return (
    <Modal
      open={open}
      closable={false}
      centered
      maskClosable={false}
      footer={null}
      width={width}
      classNames={{
        content:
          "px-6! pt-9! pb-7! rounded-[20px]! border border-white! bg-white/85! shadow-[12px_12px_48px_0_rgba(32,32,32,0.24)]! backdrop-blur-[40px]!",
        footer: "flex justify-start",
      }}
    >
      <div>
        <div className="text-center text-[20px] text-black font-medium not-italic leading-normal">
          {title}
        </div>

        <p className="mt-3 text-black text-sm font-normal leading-normal text-center">
          {content}
        </p>

        <div className="mt-6 flex gap-3">
          <Button
            type="border"
            className="h-10 flex-1 bg-transparent border-black/15"
            style={
              backButtonWidth ? { width: backButtonWidth, flex: "none" } : {}
            }
            onClick={onBack}
          >
            {backText || "Back"}
          </Button>
          <Button
            type="primary"
            className="h-10 flex-1"
            disabled={countdown > 0}
            onClick={onContinue}
          >
            {continueText || "Continue"}
            {countdown > 0 ? ` (${countdown}s)` : ""}
          </Button>
        </div>

        <CloseIcon
          className="absolute top-4 right-4 w-5 h-5 cursor-pointer text-black/20 hover:text-black/60"
          onClick={onBack}
          aria-label="Close"
        />
      </div>
    </Modal>
  );
}

export type ConfirmOptions = Omit<
  ConfirmModalProps,
  "open" | "onContinue" | "onBack"
>;

export const confirmModal = {
  confirm: (options: ConfirmOptions): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (typeof document === "undefined") {
        resolve();
        return;
      }

      const container = document.createElement("div");
      document.body.appendChild(container);
      const root = createRoot(container);

      const render = (open: boolean) => {
        root.render(
          <ConfirmModal
            open={open}
            title={options.title}
            content={options.content}
            continueText={options.continueText}
            backText={options.backText}
            width={options.width}
            backButtonWidth={options.backButtonWidth}
            confirmCountdown={options.confirmCountdown}
            onContinue={handleContinue}
            onBack={handleBack}
          />,
        );
      };

      const destroy = () => {
        // 先触发关闭动画
        render(false);
        // 等待动画完成后再销毁
        setTimeout(() => {
          root.unmount();
          if (container.parentNode) {
            container.parentNode.removeChild(container);
          }
        }, 300); // antd Modal 默认动画时间
      };

      const handleContinue = () => {
        destroy();
        resolve();
      };

      const handleBack = () => {
        destroy();
        reject();
      };

      // 先渲染为 closed 状态，然后在下一帧切换为 open，以触发动画
      render(false);
      requestAnimationFrame(() => {
        render(true);
      });
    });
  },
};

export default ConfirmModal;
